// S8 — one checklist row: its verdict, WHY it holds, and the actions §3.6
// allows on it (Module 02 §4 S8).
//
// The row never invents a verdict. It renders what the evaluator decided:
//  - `passed` / `failed` with the evidence counts the decision rested on;
//  - `pending` with the owning module it is BLOCKED ON when `detail.checkable`
//    is false (D-033: an unwired module is never a pass and never a failure the
//    user could fix — so the row says which module, and offers only a waiver);
//  - `waived` with the reason someone accepted the gap.
//
// Actions mirror §7 + §3.6 and are hidden, never merely disabled, when the role
// cannot use them: waive (blocking items group_admin-only, re-asserted by the
// API with 403 BLOCKING_WAIVE_FORBIDDEN), unwaive, and manual-set for `manual`
// items. Attesting `channels.floor_pwa_tested` as passed while Module 04's
// verifier is unbound is refused by the API (422 CHECK_NOT_AVAILABLE) — the
// button is disabled with that explanation rather than offering a fake check.

import { enumLabel } from "../../../../lib/enum-label";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { formatTimestamp } from "../../../../lib/datetime";
import { WizardDialog } from "../../components/wizard-dialog";
import {
  useManualSetChecklistItem,
  useUnwaiveChecklistItem,
  useWaiveChecklistItem,
} from "../../hooks/onboarding-queries";
import {
  checklistItemLabelKey,
  isCode,
  problemStatus,
  WIZARD_PROBLEM_CODES,
} from "../../lib/wizard-problems";
import {
  blockedOnModuleKey,
  evidenceOf,
  fixRouteOf,
  isCheckable,
  isVacuous,
  moduleLabelKey,
  ruleKeyOf,
  type ChecklistItem,
} from "./checklist-catalog";

/** Minimum waive-reason length — contract, service AND column CHECK (§3.6). */
export const WAIVE_REASON_MIN_LENGTH = 10;

export interface ChecklistItemRowProps {
  item: ChecklistItem;
  /** §7 waive gate, blocking items included (`perms.canWaive(blocking)`). */
  canWaive: boolean;
  /** §7 `onboarding.checklist.manual_set` (+ group_admin for blocking items). */
  canManualSet: boolean;
  /** The tenant is live — every mutation answers 409; hide them all (§4 S8). */
  frozen: boolean;
}

export function ChecklistItemRow({ item, canWaive, canManualSet, frozen }: ChecklistItemRowProps) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [reason, setReason] = useState("");
  const waive = useWaiveChecklistItem();
  const unwaive = useUnwaiveChecklistItem();
  const manualSet = useManualSetChecklistItem();

  const blockedOn = blockedOnModuleKey(item);
  const checkable = isCheckable(item);
  const fixRoute = fixRouteOf(item);
  const evidence = evidenceOf(item);
  const failure: unknown = waive.error ?? unwaive.error ?? manualSet.error;
  const busy = waive.isPending || unwaive.isPending || manualSet.isPending;
  const reasonOk = reason.trim().length >= WAIVE_REASON_MIN_LENGTH;

  const showWaive = !frozen && canWaive && item.status !== "waived";
  const showUnwaive = !frozen && canWaive && item.status === "waived";
  const showManual = !frozen && canManualSet && item.kind === "manual";

  return (
    <li className="chk-item" data-status={item.status} data-testid="checklist-item">
      <div className="chk-item-head">
        <div className="chk-item-titles">
          <p className="chk-item-title">
            <StatusIcon status={item.status} />
            {t(checklistItemLabelKey(item.item_key), { defaultValue: item.item_key })}
          </p>
          <p className="chk-item-meta">
            <StatusChip variant={ITEM_VARIANT[item.status] ?? "neutral"} data-status={item.status}>
              {t(`onboarding.checklist.itemStatus.${item.status}`)}
            </StatusChip>
            {item.blocking ? (
              <StatusChip variant="bad" dot={false}>
                {t("onboarding.checklist.blocking")}
              </StatusChip>
            ) : null}
            <span className="chk-weight">
              {t("onboarding.checklist.weight", { weight: item.weight })}
            </span>
            {item.kind === "manual" ? (
              <span className="chk-weight">{t("onboarding.checklist.manualItem")}</span>
            ) : null}
          </p>
        </div>
        <button
          type="button"
          className="chk-expand"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded ? t("onboarding.checklist.hideDetail") : t("onboarding.checklist.showDetail")}
        </button>
      </div>

      {/* The verdict's one-line WHY is always visible; the counts fold away. */}
      <p className="chk-item-why">{t(ruleKeyOf(item), { defaultValue: item.item_key })}</p>

      {/* §4 S8: name what is missing AND where to fix it. Only for items that
          are not already met and that a user can actually act on. */}
      {fixRoute !== null && item.status !== "passed" && item.status !== "waived" ? (
        <p className="chk-item-fix">
          <Link to={fixRoute} className="wizard-header-link" data-testid="checklist-fix-link">
            {t("onboarding.checklist.fixThis")}
          </Link>
        </p>
      ) : null}

      {blockedOn !== undefined ? (
        <p className="chk-blocked" data-testid="checklist-blocked-on">
          {t("onboarding.checklist.blockedOnModule", {
            module: t(moduleLabelKey(blockedOn), { defaultValue: blockedOn }),
          })}
        </p>
      ) : null}

      {isVacuous(item) ? <p className="chk-note">{t("onboarding.checklist.vacuous")}</p> : null}

      {item.status === "waived" ? (
        <p className="chk-note" data-testid="checklist-waived">
          {t("onboarding.checklist.waivedReason", { reason: item.waived_reason ?? "" })}
        </p>
      ) : null}

      {expanded ? (
        <div className="chk-detail">
          <p className="chk-note">
            {item.evaluated_at !== null
              ? t("onboarding.checklist.evaluatedAt", {
                  time: formatTimestamp(item.evaluated_at, i18n.language),
                })
              : t("onboarding.checklist.neverEvaluatedItem")}
          </p>
          {evidence.length > 0 ? (
            <dl className="chk-evidence">
              {evidence.map((entry) => (
                <div className="chk-evidence-row" key={entry.field}>
                  <dt>{enumLabel(t, "onboarding.checklist.evidence", entry.field)}</dt>
                  <dd>{entry.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="chk-note">{t("onboarding.checklist.noEvidence")}</p>
          )}
        </div>
      ) : null}

      {showWaive || showUnwaive || showManual ? (
        <div className="chk-actions">
          {showManual ? (
            <>
              <Button
                size="sm"
                disabled={busy || !checkable}
                title={!checkable ? t("onboarding.checklist.attestUnavailable") : undefined}
                onClick={() => {
                  manualSet.mutate({ itemKey: item.item_key, status: "passed" });
                }}
              >
                {item.item_key === "channels.floor_pwa_tested"
                  ? t("onboarding.checklist.actions.testScanOk")
                  : t("onboarding.checklist.actions.attestPassed")}
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => {
                  manualSet.mutate({ itemKey: item.item_key, status: "failed" });
                }}
              >
                {t("onboarding.checklist.actions.attestFailed")}
              </Button>
              {!checkable ? (
                <p className="chk-note">{t("onboarding.checklist.attestUnavailable")}</p>
              ) : null}
            </>
          ) : null}
          {showWaive ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                setWaiving(true);
              }}
            >
              {t("onboarding.checklist.actions.waive")}
            </Button>
          ) : null}
          {showUnwaive ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                unwaive.mutate({ itemKey: item.item_key });
              }}
            >
              {t("onboarding.checklist.actions.unwaive")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {waiving ? (
        <WizardDialog
          testId="checklist-waive-form"
          destructive={item.blocking}
          title={t("onboarding.checklist.waiveTitle")}
          confirmLabel={
            waive.isPending
              ? t("onboarding.checklist.actions.waiving")
              : t("onboarding.checklist.actions.waiveConfirm")
          }
          cancelLabel={t("onboarding.checklist.actions.cancel")}
          busy={!reasonOk || waive.isPending}
          onConfirm={() => {
            if (!reasonOk) return;
            waive.mutate(
              { itemKey: item.item_key, reason: reason.trim() },
              {
                onSuccess: () => {
                  setWaiving(false);
                  setReason("");
                },
              },
            );
          }}
          onCancel={() => {
            setWaiving(false);
          }}
        >
          <p className="chk-note">
            {item.blocking
              ? t("onboarding.checklist.waiveBlockingBody")
              : t("onboarding.checklist.waiveBody")}
          </p>
          <Field label={t("onboarding.checklist.waiveReasonLabel")}>
            <textarea
              className="ds-input"
              value={reason}
              rows={3}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
          <p className="chk-note">
            {t("onboarding.checklist.waiveReasonHint", {
              min: WAIVE_REASON_MIN_LENGTH,
              count: reason.trim().length,
            })}
          </p>
        </WizardDialog>
      ) : null}

      {failure !== null && failure !== undefined ? (
        <p className="chk-error" role="alert" data-testid="checklist-item-error">
          {itemErrorMessage(failure, t)}
        </p>
      ) : null}
    </li>
  );
}

type TFn = ReturnType<typeof useTranslation>["t"];

/**
 * The §5 refusals this row can provoke, each said in the user's own terms
 * rather than as a code: who may waive a blocking item, why an auto item cannot
 * be hand-set, why an attestation is unavailable, and the frozen wizard.
 */
export function itemErrorMessage(error: unknown, t: TFn): string {
  if (isCode(error, WIZARD_PROBLEM_CODES.blockingWaiveForbidden)) {
    return t("onboarding.checklist.errors.blockingWaiveForbidden");
  }
  if (isCode(error, WIZARD_PROBLEM_CODES.itemNotManual)) {
    return t("onboarding.checklist.errors.itemNotManual");
  }
  if (isCode(error, WIZARD_PROBLEM_CODES.checkNotAvailable)) {
    return t("onboarding.checklist.errors.checkNotAvailable");
  }
  if (isCode(error, WIZARD_PROBLEM_CODES.alreadyLive)) {
    return t("onboarding.checklist.errors.alreadyLive");
  }
  if (isCode(error, WIZARD_PROBLEM_CODES.resourceBusy)) {
    return t("onboarding.checklist.errors.busy");
  }
  if (problemStatus(error) === 422) {
    return t("onboarding.checklist.errors.reasonTooShort");
  }
  return t("onboarding.checklist.errors.generic");
}

const ITEM_VARIANT: Record<string, ChipVariant> = {
  pending: "neutral",
  passed: "ok",
  failed: "bad",
  waived: "warn",
};

function StatusIcon({ status }: { status: ChecklistItem["status"] }) {
  const glyph =
    status === "passed" ? "✓" : status === "failed" ? "✕" : status === "waived" ? "≈" : "…";
  return (
    <span className={`chk-icon chk-icon-${status}`} aria-hidden="true">
      {glyph}
    </span>
  );
}
